import { useState, useEffect, useContext } from 'react';
import { User, QrCode, LogOut, Wallet, MoreHorizontal, Clock, HeartPulse, AppWindow, Bell, Gift, ShoppingBag, Trophy } from 'lucide-react';
import QRCodeContent from './dashboard/QRCodeContent';
import ConsistencyScore from './dashboard/ConsistencyScore';
import CheckinHistory from './dashboard/CheckinHistory';
import HealthStats from './dashboard/HealthStats';
import ProfileSettings from './dashboard/ProfileSettings';
import WalletAndRewards from './dashboard/WalletAndRewards';
import NotificationCenter from './dashboard/NotificationCenter';
import YourRewards from './dashboard/YourRewards';
import Store from './dashboard/Store';
import api from '../utils/api';
import GymFeed from './dashboard/GymFeed';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { WalletProvider, WalletContext } from '../context/WalletContext';

const CustomerDashboardContent = ({ initialTab = 'qrcode' }) => {
  const { user, logout, updateUser } = useContext(AuthContext);
  const { balance: walletBalance } = useContext(WalletContext);
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(initialTab);
  const [visitedTabs, setVisitedTabs] = useState(new Set([initialTab]));
  const [hasNewFeed, setHasNewFeed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAutoLogoutBanner, setShowAutoLogoutBanner] = useState(false);

  const [gyms, setGyms] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [activeGymcode, setActiveGymcode] = useState(null);
  const [gymSwitcherOpen, setGymSwitcherOpen] = useState(false);

  const fetchGymsAndInvitations = async () => {
    try {
      const gymsRes = await api.get('/api/member/gyms');
      setGyms(gymsRes.data);

      const invRes = await api.get('/api/member/invitations');
      setInvitations(invRes.data);

      const currentActive = localStorage.getItem('fitscan_active_gymcode');
      const validActive = gymsRes.data.find(g => g.gymcode === currentActive);
      if (gymsRes.data.length > 0) {
        if (!validActive) {
          localStorage.setItem('fitscan_active_gymcode', gymsRes.data[0].gymcode);
          setActiveGymcode(gymsRes.data[0].gymcode);
        } else {
          setActiveGymcode(currentActive);
        }
      } else {
        localStorage.removeItem('fitscan_active_gymcode');
        setActiveGymcode('');
      }
    } catch (err) {
      console.error('Failed to fetch gyms and invitations:', err);
      setActiveGymcode(localStorage.getItem('fitscan_active_gymcode') || '');
    }
  };

  const handleSwitchGym = (newGymcode) => {
    localStorage.setItem('fitscan_active_gymcode', newGymcode);
    setActiveGymcode(newGymcode);
  };

  const handleAcceptInvitation = async (id) => {
    try {
      await api.post(`/api/member/invitations/${id}/accept`);
      await fetchGymsAndInvitations();
    } catch (err) {
      console.error('Failed to accept invitation:', err);
    }
  };

  const handleRejectInvitation = async (id) => {
    try {
      await api.post(`/api/member/invitations/${id}/reject`);
      await fetchGymsAndInvitations();
    } catch (err) {
      console.error('Failed to reject invitation:', err);
    }
  };

  const handleTabChange = (key) => {
    setActiveMenu(key);
    setVisitedTabs(prev => new Set(prev).add(key));
    if (key === 'gymfeed') {
      localStorage.setItem('fitscan_last_feed_visit', Date.now().toString());
      setHasNewFeed(false); // clear dot on visit
    }
  };

  useEffect(() => {
    if (user?.role === 'User' && localStorage.getItem('fitscan_auto_logout_pending') === 'true') {
      localStorage.removeItem('fitscan_auto_logout_pending');
      setShowAutoLogoutBanner(true);
      setTimeout(() => setShowAutoLogoutBanner(false), 3000);
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === 'User') {
      fetchGymsAndInvitations();
    }
  }, [user]);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleSWMessage = (event) => {
        if (event.data && event.data.type === 'REFETCH_INVITATIONS') {
          fetchGymsAndInvitations();
        }
      };
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      };
    }
  }, []);

  useEffect(() => {
    if (!user?.username) return;
    const fetchProfilePhoto = async () => {
      try {
        const response = await api.post('/api/profile/userprofile', { Username: user.username });
        const data = response.data;
        if (data.photoUrl) {
          updateUser({ photoUrl: data.photoUrl });
        }
      } catch (err) {
        console.error('Failed to sync profile photo on mount:', err);
      }
    };
    fetchProfilePhoto();
  }, [user?.username, updateUser]);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="flex h-screen bg-[#101010] text-gray-200">
      {/* Sidebar Navigation */}
      <div className="w-64 bg-[#181818] border-r border-[#2b2b2b] p-4 hidden md:block">
        <div className="flex items-center gap-3 mb-8 p-2">
          <span className="bg-[#6CD800] rounded-xl p-2">
            <svg width="24" height="24" viewBox="0 0 448 512">
              <path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z" fill="white" />
            </svg>
          </span>
          <span className="text-xl font-bold">FitScan</span>
        </div>

        <nav className="space-y-1 bg-[#181818]">
          {[
            { icon: <QrCode size={20} />, label: "My QR Code", key: "qrcode" },
            { icon: <AppWindow size={20} />, label: "Gym Feed", key: "gymfeed" },
            { icon: <Trophy size={20} />, label: "Consistency Score", key: "consistencyscore" },
            { icon: <Gift size={20} />, label: "Your Rewards", key: "rewards" },
            { icon: <ShoppingBag size={20} />, label: "Store", key: "store" },
            { icon: <HeartPulse size={20} />, label: "Health Stats", key: "healthstats" },
            { icon: <Clock size={20} />, label: "Check-in History", key: "checkinhistory" },
            { icon: <Wallet size={20} />, label: "Wallet", key: "wallet" },
            { icon: <User size={20} />, label: "Profile", key: "profile" },
          ].map((item) => {
            const isActive = activeMenu === item.key;

            return (
              <button
                key={item.label}
                onClick={() => handleTabChange(item.key)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg w-full transition-all duration-200 ease-in-out border-0 cursor-pointer text-left
                  ${isActive ? 'bg-[#222] text-yellow-400 scale-105 font-medium ' : 'bg-[#181818] hover:bg-[#222] text-gray-300'}
                `}
              >
                <div className={`relative opacity-90`}>
                  {item.icon}
                  {item.key === 'gymfeed' && hasNewFeed && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#6CD800] rounded-full border border-[#181818]" />
                  )}
                </div>
                <span>{item.label}</span>
              </button>
            );
          })}

          <button
            key="LogOut"
            onClick={handleLogout}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg w-full transition-all duration-200 ease-in-out border-0 cursor-pointer text-left bg-[#181818]
              ${activeMenu === "LogOut" ? 'bg-[#222] text-yellow-400 scale-105 font-medium ' : 'hover:bg-[#222] text-gray-300'}
            `}
          >
            <span className="opacity-90"><LogOut size={20} /></span>
            <span>Log Out</span>
          </button>
        </nav>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#181818] border-t border-[#2b2b2b] flex justify-around p-2 z-50">
        {[
          { icon: <QrCode size={20} />, label: "QR", key: "qrcode" },
          { icon: <AppWindow size={20} />, label: "Gym Feed", key: "gymfeed" },
          { icon: <Trophy size={28} strokeWidth={1.5} />, label: "Score", key: "consistencyscore" },
          { icon: <HeartPulse size={20} />, label: "Health", key: "healthstats" },
          { icon: <MoreHorizontal size={20} />, label: "More", key: "More", isMenuTrigger: true },
        ].map((item) => {
          const isActive = activeMenu === item.key;
          return (
            <button
              key={item.label}
              onClick={() => {
                if (item.isMenuTrigger) {
                  setMobileMenuOpen(!mobileMenuOpen);
                } else {
                  handleTabChange(item.key);
                  setMobileMenuOpen(false);
                }
              }}
              className={`flex flex-col items-center p-2 text-xs border-0 bg-transparent transition-transform duration-200 cursor-pointer ${isActive ? 'text-yellow-400 scale-110' : 'text-gray-400 opacity-70'
                }`}
            >
              <div className="relative">
                {item.icon}
                {item.key === 'gymfeed' && hasNewFeed && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#6CD800] rounded-full border border-[#181818]" />
                )}
              </div>
              <span className="mt-1">{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-[100] bg-black/55 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="absolute top-0 right-0 w-64 h-full bg-[#181818] border-l border-[#2b2b2b] p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="space-y-1">
              <div className="flex items-center gap-3 mb-8 p-2">
                <span className="bg-[#6CD800] rounded-xl p-2">
                  <svg width="24" height="24" viewBox="0 0 448 512">
                    <path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z" fill="white" />
                  </svg>
                </span>
                <span className="text-xl font-bold">FitScan</span>
              </div>

              {[
                { icon: <Gift size={20} />, label: "Your Rewards", key: "rewards" },
                { icon: <ShoppingBag size={20} />, label: "Store", key: "store" },
                { icon: <Clock size={20} />, label: "Check-in History", key: "checkinhistory" },
                { icon: <Wallet size={20} />, label: "Wallet", key: "wallet" },
                { icon: <User size={20} />, label: "Profile", key: "profile" },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    handleTabChange(item.key);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg border-0 cursor-pointer text-left bg-transparent ${activeMenu === item.key ? 'bg-[#222] text-[#6CD800]' : 'hover:bg-[#222] text-gray-300'
                    }`}
                >
                  <div className="relative">
                    {item.icon}
                    {item.key === 'gymfeed' && hasNewFeed && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#6CD800] rounded-full border border-[#181818]" />
                    )}
                  </div>
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto px-4 pt-1.5 pb-24 md:px-6 md:pt-4 md:pb-6">
        <header className="flex justify-between items-center py-1 mb-2 md:py-2 md:mb-6">
          <div>
            {user?.role === 'User' && gyms.length > 0 && (
              <div className="relative">
                {gyms.length >= 2 ? (
                  <div className="relative">
                    <button
                      onClick={() => setGymSwitcherOpen(!gymSwitcherOpen)}
                      className="flex items-center gap-2 text-sm font-semibold text-lime-400 hover:text-lime-300 transition-colors bg-transparent border-0 p-0 cursor-pointer"
                    >
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-lime-950/50 text-lime-400">
                        📍
                      </span>
                      <span>Gym: {gyms.find(g => g.gymcode === activeGymcode)?.gymName || 'Select Gym'}</span>
                      <span className="text-xs">▼</span>
                    </button>
                    {gymSwitcherOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setGymSwitcherOpen(false)} />
                        <div className="absolute left-0 mt-2 w-56 rounded-xl bg-[#1c1c1c] border border-[#2b2b2b] shadow-2xl p-1.5 z-50 animate-scale-up">
                          {gyms.map((gym) => (
                            <button
                              key={gym.gymcode}
                              onClick={() => {
                                handleSwitchGym(gym.gymcode);
                                setGymSwitcherOpen(false);
                              }}
                              className={`flex flex-col items-start w-full px-3 py-2 rounded-lg text-left border-0 cursor-pointer transition-colors ${gym.gymcode === activeGymcode
                                ? 'bg-lime-950/30 text-lime-400'
                                : 'bg-transparent text-gray-300 hover:bg-[#282828] hover:text-white'
                                }`}
                            >
                              <span className="font-bold text-xs">{gym.gymName}</span>
                              <span className="text-[10px] text-gray-500 truncate w-full">{gym.location}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-400 font-medium">
                    <span className="text-xs">📍</span>
                    <span>Gym: {gyms[0].gymName}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 sm:gap-6">
            {/* Wallet Balance */}
            <div
              className="flex items-center gap-2.5 cursor-pointer group bg-[#181818] border border-[#2b2b2b] px-3 py-1.5 rounded-full hover:border-[#6CD800]/50 transition-all"
              onClick={() => handleTabChange('wallet')}
            >
              <div className="w-7 h-7 rounded-full bg-[#6CD800] flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                <Wallet size={14} className="text-[#181f10]" />
              </div>
              <span className="text-sm font-semibold text-gray-200">{walletBalance}</span>
            </div>

            {/* Notification Bell */}
            <button
              className="relative p-2.5 rounded-full bg-[#181818] border border-[#2b2b2b] hover:border-[#6CD800]/50 transition-all group cursor-pointer"
              aria-label="Notifications"
              onClick={() => setNotifOpen(true)}
            >
              <Bell size={18} className="text-gray-400 group-hover:text-[#6CD800] transition-colors" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5">
                  <span className="animate-pulse absolute inline-flex h-full w-full rounded-full bg-[#6CD800]/40 opacity-75 blur-[2px]"></span>
                  <span className="relative inline-flex rounded-full h-5 w-5 bg-[#6CD800] text-zinc-950 font-bold text-[10px] items-center justify-center flex-shrink-0">
                    {unreadCount}
                  </span>
                </span>
              )}
            </button>

            {/* Profile Avatar */}
            <div
              className="flex items-center gap-3 cursor-pointer group"
              onClick={() => handleTabChange('profile')}
            >
              <div className="w-10 h-10 rounded-full bg-[#222] border-2 border-[#2b2b2b] p-0.5 group-hover:border-[#6CD800] transition-all overflow-hidden">
                {user?.photoUrl ? (
                  <img
                    src={user.photoUrl.replace(/\\/g, '/')}
                    alt={user.name || 'Profile'}
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextSibling?.style?.removeProperty('display');
                    }}
                  />
                ) : null}
                {(!user?.photoUrl) && (
                  <div className="w-full h-full rounded-full bg-[#6CD800] flex items-center justify-center">
                    <User size={20} className="text-[#181f10]" />
                  </div>
                )}
              </div>
              <div className="hidden lg:block">
                <p className="text-sm font-bold text-white leading-none">{user.name}</p>
                <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">Member</p>
              </div>
            </div>
          </div>
        </header>

        {showAutoLogoutBanner && (
          <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[999] w-[90%] max-w-sm bg-[#1a1a1a] border border-yellow-500/40 rounded-xl px-4 py-3 flex items-center gap-3 shadow-xl animate-fade-in-down">
            <span className="text-yellow-400 text-lg flex-shrink-0">⚠️</span>
            <p className="text-sm font-medium text-yellow-300 leading-snug">
              You were automatically logged out at 1:00 AM. Your data is safe.
            </p>
          </div>
        )}

        {/* Invitations Banner */}
        {invitations.length > 0 && (
          <div className="flex flex-col gap-3 mb-6">
            {invitations.map((inv) => (
              <div
                key={inv.id}
                className="bg-[#181818] border border-[#6CD800]/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-lg animate-fade-in"
              >
                <div>
                  <h3 className="text-white font-bold text-base flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#6CD800] animate-ping" />
                    Invitation from {inv.gymName}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    📍 {inv.location || 'Unknown Location'}
                  </p>
                  {inv.planname && (
                    <span className="inline-block bg-[#222] text-[#6CD800] text-[10px] font-semibold mt-2 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Plan: {inv.planname}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleAcceptInvitation(inv.id)}
                    className="bg-[#6CD800] hover:bg-[#5bb300] text-zinc-950 font-bold px-4 py-2 rounded-lg text-xs md:text-sm transition-all border-0 cursor-pointer"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleRejectInvitation(inv.id)}
                    className="bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-300 font-bold px-4 py-2 rounded-lg text-xs md:text-sm transition-all border border-[#3b3b3b] cursor-pointer"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <section className={`bg-[#181818] border border-[#2b2b2b] rounded-xl mb-4 md:mb-8 ${activeMenu === 'profile' ? 'p-3 pt-2 md:p-4' : 'p-3 md:p-6'}`}>
          {visitedTabs.has('qrcode') && (
            <div className={activeMenu === 'qrcode' ? '' : 'hidden'}>
              {activeGymcode === null ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lime-400"></div>
                  <p className="text-gray-400 mt-4 text-sm font-medium">Loading QR Code...</p>
                </div>
              ) : activeGymcode ? (
                <QRCodeContent activeGymcode={activeGymcode} gyms={gyms} handleSwitchGym={handleSwitchGym} />
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <p className="text-red-400 font-medium mb-2">No Active Gym</p>
                  <p className="text-gray-400 text-sm max-w-xs mx-auto">
                    Please accept a gym invitation or contact your gym administrator to get started.
                  </p>
                </div>
              )}
            </div>
          )}
          {visitedTabs.has('gymfeed') && <div className={activeMenu === 'gymfeed' ? '' : 'hidden'}><GymFeed key={activeGymcode} activeGymcode={activeGymcode} onFeedLoaded={(newestPostDate) => {
            const lastVisit = parseInt(localStorage.getItem('fitscan_last_feed_visit') || '0');
            const postTime = new Date(newestPostDate).getTime();
            if (postTime > lastVisit) setHasNewFeed(true);
          }} /></div>}
          {visitedTabs.has('consistencyscore') && <div className={activeMenu === 'consistencyscore' ? '' : 'hidden'}><ConsistencyScore key={activeGymcode} onTabChange={handleTabChange} /></div>}
          {visitedTabs.has('rewards') && <div className={activeMenu === 'rewards' ? '' : 'hidden'}><YourRewards key={activeGymcode} /></div>}
          {visitedTabs.has('store') && <div className={activeMenu === 'store' ? '' : 'hidden'}><Store key={activeGymcode} /></div>}
          {visitedTabs.has('healthstats') && <div className={activeMenu === 'healthstats' ? '' : 'hidden'}><HealthStats key={activeGymcode} /></div>}
          {visitedTabs.has('wallet') && <div className={activeMenu === 'wallet' ? '' : 'hidden'}><WalletAndRewards key={activeGymcode} /></div>}
          {visitedTabs.has('checkinhistory') && <div className={activeMenu === 'checkinhistory' ? '' : 'hidden'}><CheckinHistory key={activeGymcode} /></div>}
          {visitedTabs.has('profile') && <div className={activeMenu === 'profile' ? '' : 'hidden'}><ProfileSettings key={activeGymcode} /></div>}
        </section>
      </div>

      <NotificationCenter
        isOpen={notifOpen}
        onClose={() => setNotifOpen(false)}
        onNavigate={(menu) => {
          handleTabChange(menu);
          setNotifOpen(false);
        }}
        username={user?.username}
        onUnreadCountChange={setUnreadCount}
      />
    </div>
  );
};

const CustomerDashboard = ({ initialTab = 'qrcode' }) => {
  return (
    <WalletProvider>
      <CustomerDashboardContent initialTab={initialTab} />
    </WalletProvider>
  );
};

export default CustomerDashboard;